import appConfig from '../../../defs/appConfig';
import { config } from '../../../game/defs/config';
import { GoalId, getGoalMap } from '../../../game/defs/goal';
import { BasicScreen } from '../../../lib/ui/BasicScreen';
import { Container, Sprite } from '../../../odie';
import { BlockButton } from '../../ui/BlockButton';

// types
//-----------------------------------------------------------------------------
export type GoalPickerPopupOptions = {
  selectedId: GoalId;
  onOk: (id: GoalId) => void;
  width?: number;
  height?: number;
};

/*
    goal picker
*/
export class GoalPickerPopup extends BasicScreen {
  // fields
  //-------------------------------------------------------------------------
  private _buttons: Partial<Record<GoalId, BlockButton>> = {};
  private _selectedId!: GoalId;
  private _onOk?: (id: GoalId) => void;
  protected onClose?: () => void;
  protected content!: Container;
  protected isClosing = false;

  // impl
  //-------------------------------------------------------------------------
  // @ts-ignore
  public override assets(): string[] {
    return [...Object.values(getGoalMap()), ...BlockButton.assets()];
  }

  // @ts-ignore
  public override async prepare(options: GoalPickerPopupOptions) {
    const gridCols = 7;
    const goalCount = Object.keys(getGoalMap()).length;
    const gridRows = Math.ceil(goalCount / gridCols);
    const gridWidth = gridCols * config.tile.size;
    const gridHeight = gridRows * config.tile.size + 80;
    const contentWidth = gridWidth;
    const contentHeight = gridHeight;

    this.root.width = appConfig.size.width;
    this.root.height = appConfig.size.height;
    this._spawnScreen();
    this._spawnContent({
      ...options,
      width: contentWidth,
      height: contentHeight,
    });
    this._spawnBackground({
      ...options,
      width: contentWidth,
      height: contentHeight,
    });

    this._onOk = options.onOk;

    // spawn goal buttons
    this._spawnGoalButtons(gridRows, gridCols, config.tile.size);
  }

  private _spawnScreen() {
    // create translucent screen
    const screen = new Sprite();
    screen.width = this.root.width;
    screen.height = this.root.height;
    screen.view.updateOpts({ backgroundColor: 'rgba(0, 0, 0, 0.8)' });
    this.root.addChild(screen);

    // tapping the screen triggers close request
    screen.setOnTap(() => this.close());
    screen.interactive = true;
  }

  private _spawnContent(options: GoalPickerPopupOptions) {
    // spawn center pivoted for animation reasons
    const content = (this.content = new Container());
    content.width = options.width ?? 600;
    content.height = options.height ?? 600;
    content.x = this.root.width * 0.5 - content.width * 0.5;
    content.y = this.root.height * 0.5 - content.height * 0.5;
    this.root.addChild(content);
  }

  protected _spawnBackground(options: GoalPickerPopupOptions) {
    const bg = new Container();

    bg.width = options.width ?? 600;
    bg.height = options.height ?? 600;
    bg.view.updateOpts({
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    });

    // this blocks input from popup
    bg.interactive = true;
    this.content.addChild(bg);
  }

  // private: actions
  //-------------------------------------------------------------------------
  private _setSelected(goalId: GoalId) {
    // deselect last
    const button = this._buttons[this._selectedId];
    if (button) button.selected = false;

    // update state
    this._selectedId = goalId;

    // select new
    this._buttons[goalId]!.selected = true;
    this._onOk?.(goalId);
    this.close();
  }

  // private: scene
  //-------------------------------------------------------------------------
  private _spawnGoalButtons(rows: number, cols: number, cellSize: number) {
    const goalIds = Object.keys(getGoalMap()).filter(
      (id) => !!getGoalMap()[id as GoalId],
    ) as GoalId[];

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const goalIndex = y * cols + x;
        const goalId = goalIds[goalIndex];

        if (!goalId) {
          break;
        }

        const xPos = x * cellSize;
        const yPos = y * cellSize;

        this._buttons[goalId] = this._spawnGoalButton(xPos, yPos, goalId);
      }
    }
  }

  private _spawnGoalButton(x: number, y: number, blockId: GoalId) {
    const button = new BlockButton({
      type: 'goal',
      def: { id: blockId },
    });

    // reset button content origin to 0 (its pivot is already at half width, half height)
    // then position the button in the grid cell and scale down slightly
    button.content.x = 0;
    button.content.y = 0;
    button.x = x;
    button.y = y;
    button.scaleX = 0.9;
    button.scaleY = 0.9;
    button.onTap = () => {
      this._setSelected(blockId);
    };
    button.initListeners();

    this.content.addChild(button);

    return button;
  }

  public async show() {
    this.root.visible = true;
  }

  public close() {
    this.view.view.removeFromSuperview();
  }
}
